import React, { useState } from "react";
import { db } from "../lib/instant";
import { Tag, X, Check } from "lucide-react";
import toast from "react-hot-toast";

export default function CouponInput({ subtotal, onApply, theme }) {
  const primaryColor = theme?.primaryColor || "#6b2118";
  const secondaryColor = theme?.secondaryColor || "#2e7d32";
  const [code, setCode] = useState("");
  const [applied, setApplied] = useState(null);
  const [checking, setChecking] = useState(false);

  const { data } = db.useQuery({ coupons: { $: { where: { active: true } } } });
  const coupons = data?.coupons || [];

  const applyCode = async () => {
    if (!code.trim()) return;
    setChecking(true);
    const coupon = coupons.find(c => c.code.toUpperCase() === code.toUpperCase().trim());
    if (!coupon) { toast.error("Invalid coupon code"); setChecking(false); return; }
    if (!coupon.active) { toast.error("This coupon is no longer active"); setChecking(false); return; }
    if (coupon.expiresAt && new Date(coupon.expiresAt) < new Date()) { toast.error("This coupon has expired"); setChecking(false); return; }
    if (coupon.minOrder && subtotal < coupon.minOrder) { toast.error(`Minimum order ₹${coupon.minOrder} required`); setChecking(false); return; }

    const discount = coupon.type === "percent"
      ? (subtotal * coupon.value) / 100
      : Math.min(coupon.value, subtotal);

    setApplied({ ...coupon, discount });
    onApply({ ...coupon, discount });
    toast.success(`Coupon applied! You save ₹${discount.toFixed(2)}`);

    // Increment used count
    try {
      await db.transact([db.tx.coupons[coupon.id].update({ usedCount: (coupon.usedCount || 0) + 1 })]);
    } catch {}
    setChecking(false);
  };

  const removeCoupon = () => {
    setApplied(null);
    setCode("");
    onApply(null);
    toast("Coupon removed");
  };

  if (applied) return (
    <div style={{ background: "#e8f5e9", border: "1.5px solid #4caf50", borderRadius: 12, padding: "14px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <Check size={18} color="#388e3c" />
        <div>
          <div style={{ fontWeight: 700, color: "#388e3c", fontSize: "0.9rem", fontFamily: "monospace" }}>{applied.code}</div>
          <div style={{ fontSize: "0.75rem", color: "#555" }}>
            {applied.type === "percent" ? `${applied.value}% OFF` : `₹${applied.value} OFF`} applied · You save ₹{applied.discount.toFixed(2)}
          </div>
        </div>
      </div>
      <button onClick={removeCoupon} style={{ background: "none", border: "none", cursor: "pointer", color: "#888" }}>
        <X size={16}/>
      </button>
    </div>
  );

  return (
    <div>
      <div style={{ display: "flex", gap: 10 }}>
        <div style={{ position: "relative", flex: 1 }}>
          <Tag size={15} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#bbb" }} />
          <input
            value={code}
            onChange={e => setCode(e.target.value.toUpperCase())}
            onKeyDown={e => e.key === "Enter" && applyCode()}
            placeholder="Enter coupon code"
            style={{ width: "100%", padding: "11px 12px 11px 36px", border: "1.5px solid #e0e0e0", borderRadius: 10, fontSize: "0.88rem", outline: "none", fontFamily: "monospace", letterSpacing: 1 }}
          />
        </div>
        <button onClick={applyCode} disabled={checking || !code.trim()} style={{
          padding: "11px 20px", background: code.trim() ? primaryColor : "#e0e0e0",
          color: "#fff", border: "none", borderRadius: 10, fontWeight: 700,
          fontSize: "0.88rem", cursor: code.trim() ? "pointer" : "not-allowed",
          whiteSpace: "nowrap",
        }}>{checking ? "Checking..." : "Apply"}</button>
      </div>
    </div>
  );
}
